'use client';

import Link from 'next/link';
import { z } from 'zod';

import CallToAction from '~/components/atoms/CallToAction';
import Heading from '~/components/atoms/Heading';
import Wrap from '~/components/atoms/Wrap';
import ExpandToEdge from '~/components/molecules/ExpandToEdge';
import LayoutGrid from '~/components/molecules/LayoutGrid';
import Media from '~/components/molecules/Media';
import { modelPropertyType } from '~/lib/api/models';
import routeGenerator from '~/lib/api/routes';
import { useHydrationSafeResponsive } from '~/lib/hooks/useResponsive';

type Props = NonNullable<
  z.infer<
    typeof modelPropertyType.schema
  >['propertyType']['propertyTypeMeta']['soldPropertiesSlice']
>;

const SoldProperties: React.FC<Props> = ({
  leftMedia,
  title,
  rightMedia,
  linkedCategory: linkedCategoryList,
  linkText,
}) => {
  const isMobile = useHydrationSafeResponsive('mobile');
  const linkedCategory = (linkedCategoryList || []).at(0);

  return (
    <Wrap
      with={(children) => (
        <div className="bg-brand-tertiary mb-2 flex flex-col gap-y-10">
          {children}
        </div>
      )}
      when={Boolean(isMobile)}
      fallback={(children) => (
        <LayoutGrid
          as="section"
          className="bg-brand-tertiary my-2"
          classNames={{ inner: 'max-lg:gap-y-10 items-center' }}
        >
          {children}
        </LayoutGrid>
      )}
    >
      {leftMedia && (
        <ExpandToEdge
          side="left"
          className="relative col-span-2 col-start-1 h-80 lg:col-span-4 lg:h-full"
        >
          <Media
            media={leftMedia.media}
            imageProps={{
              fill: true,
              sizes:
                leftMedia.media.type === 'image'
                  ? leftMedia.media.image?.node.sizes
                  : '',
              className: 'w-full h-full object-cover',
            }}
            videoProps={{
              className: 'w-full h-full object-cover',
              autoPlay: true,
              muted: true,
              playsInline: true,
              loop: true,
              controls: false,
            }}
            reactPlayerProps={{
              className: '!w-full !h-full !object-cover',
              playing: true,
              playsInline: true,
              loop: true,
              muted: true,
            }}
          />
        </ExpandToEdge>
      )}

      <div className="relative col-span-2 col-start-1 flex flex-col items-center gap-y-6 pb-10 lg:col-span-5 lg:col-start-8 lg:items-start lg:gap-y-16 lg:pt-40 lg:pb-36">
        {title && (
          <Heading
            as="h2"
            level={5}
            className="text-brand-primary w-50 lg:w-auto"
            html={title}
          />
        )}

        {rightMedia && (
          <div className="relative aspect-300/420 w-50 max-w-75 lg:w-full">
            <Media
              media={rightMedia.media}
              imageProps={{
                fill: true,
                sizes:
                  rightMedia.media.type === 'image'
                    ? rightMedia.media.image?.node.sizes
                    : '',
                className: 'w-full h-full object-cover',
              }}
              videoProps={{
                className: 'w-full h-full object-cover',
                autoPlay: true,
                muted: true,
                playsInline: true,
                loop: true,
                controls: false,
              }}
              reactPlayerProps={{
                className: '!w-full !h-full !object-cover',
                playing: true,
                playsInline: true,
                loop: true,
                muted: true,
              }}
            />
          </div>
        )}

        {linkedCategory && (
          <div className="relative w-50 lg:w-auto">
            <CallToAction
              as={Link}
              href={routeGenerator.propertyType({
                category: linkedCategory.slug,
              })}
            >
              {linkText || linkedCategory.name}
            </CallToAction>
          </div>
        )}
      </div>
    </Wrap>
  );
};

export default SoldProperties;
